import { type Ref, ref } from 'vue'

import { useConfig } from '@/composables/config'
import { useDetectShowEvent } from '@/composables/use-detect-show-interface-event'
import type { OutOfPageAdType } from '@/modules/adv/types'
import { useLoggerStore } from '@/store/logger'
import { useLoginNudgeStore } from '@/store/login-nudge-store'
import { useMyGamesStore } from '@/store/my-games/my-games-store'
import { zipMyGame } from '@/store/my-games/utils'
import { useUserStore } from '@/store/user-store/user-store'
import type { APIGameType } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

const ADD_GAME_TIME_MS = 15_000 // 15 sec
const SHOW_LOGIN_PROMPT_DELAY_MS = 10 * 60 * 1000 // 10 minutes

/**
 * Periodic work while the game is running: ping analytics events,
 * adding the game to "my games" and the desktop login prompt.
 */
export function useGamePingSession(options: {
    game: Ref<APIGameType | undefined>
    isFullscreen: Ref<boolean>
    /** Set by the game frame on an empty ad result; attached to the next ping and reset. */
    lastAdResult: Ref<OutOfPageAdType | undefined>
    loginPromptEnabled: boolean
}) {
    const { VUE_APP_GAME_PING_INTERVAL_MS } = useConfig()
    const loggerStore = useLoggerStore()
    const userStore = useUserStore()
    const loginNudgeStore = useLoginNudgeStore()
    const myGamesStore = useMyGamesStore()
    const detectShowEvent = useDetectShowEvent()

    const addMyGameTimeOut = ref<number | undefined>(ADD_GAME_TIME_MS)
    const pingIntervalRef = ref<ReturnType<typeof setInterval>>()
    const gameStartedAt = ref<number>()
    const shouldShowLoginPrompt = ref(false)
    const isLoginPromptClosed = ref(false)

    function ping() {
        const event: LoggerEvent = {
            event: 'custom_event',
            eventName: 'ping',
            dimension1: options.isFullscreen.value ? 'fullscreen' : 'minimized',
        }

        if (options.lastAdResult.value) {
            event.adPlacement = options.lastAdResult.value
            options.lastAdResult.value = undefined
        }
        loggerStore.logEvent(event)
    }

    function waitToAddMyGame() {
        if (addMyGameTimeOut.value === undefined) {
            return
        }
        addMyGameTimeOut.value -= VUE_APP_GAME_PING_INTERVAL_MS
        if (addMyGameTimeOut.value <= 0) {
            addMyGameTimeOut.value = undefined
            myGamesStore.update(zipMyGame(options.game.value!))
        }
    }

    function checkLoginPrompt() {
        if (
            !isLoginPromptClosed.value &&
            gameStartedAt.value &&
            Date.now() - gameStartedAt.value >= SHOW_LOGIN_PROMPT_DELAY_MS &&
            !userStore.isAuthorized &&
            loginNudgeStore.desktopGameTopBarSessionsShown < 3 &&
            options.loginPromptEnabled
        ) {
            shouldShowLoginPrompt.value = true
            loginNudgeStore.markGameDesktopTopBarShown()
            detectShowEvent.trigger({
                id: 'game-login-prompt',
                interface_target: 'game-login-prompt',
            })
        }
    }

    function startPingSession() {
        gameStartedAt.value = Date.now()

        pingIntervalRef.value = setInterval(() => {
            if (!document.hidden) {
                ping()
                waitToAddMyGame()
                checkLoginPrompt()
            }
        }, VUE_APP_GAME_PING_INTERVAL_MS)
    }

    function stopPingSession() {
        clearInterval(pingIntervalRef.value || 0)
    }

    function closeLoginPrompt() {
        isLoginPromptClosed.value = true
        shouldShowLoginPrompt.value = false
    }

    function handleLogin() {
        closeLoginPrompt()
        userStore.authorizeUser('save-progress-prompt')
    }

    return {
        startPingSession,
        stopPingSession,
        shouldShowLoginPrompt,
        closeLoginPrompt,
        handleLogin,
    }
}
